import { BadgeCellRenderer, type TypeTableColumn } from '@thefarmersfront/kitchen';

import { formatNumber } from '~/utils/common';

import {
  RETURN_DISPOSAL_PROCESS_METHOD,
  type TypeReturnDisposalProcessMethod,
  type TypeReturnDisposalProcessStatus,
} from '~/constants';

import { PROCESS_STATUS_COLOR_MAP, formatExpirationOrManufacture } from './getReturnDisposalIssueItemsColumns';

import type { TypeReturnDisposalIssueItemApi } from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 클레임 처리신청 모달 - 상품정보 미리보기 컬럼 정의 (읽기 전용)
 *
 * @note 회수 컬럼셋 + `이슈발생센터`. 폐기 컬럼셋과 달리 `중량`은 미노출. 폭 합이 넘치면 가로 스크롤.
 */
const getReturnDisposalClaimItemsColumns = (): TypeTableColumn<TypeReturnDisposalIssueItemApi>[] => [
  {
    accessorKey: 'purchaseOrderCode',
    header: '발주코드',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'goodsCode',
    header: '상품코드',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'goodsName',
    header: '상품명',
    minSize: 240,
    cell: (info) => info.getValue<string>() || '-',
    meta: { flex: true },
  },
  {
    id: 'expirationOrManufacture',
    header: '소비기한/제조일자',
    cell: (info) => formatExpirationOrManufacture(info.row.original),
    meta: { columnFit: true },
  },
  {
    accessorKey: 'issueType',
    header: '이슈상세',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'issueQty',
    header: '이슈수량',
    cell: (info) => {
      const value = info.getValue<number | null>();
      return value || value === 0 ? formatNumber(value) : '-';
    },
    meta: { columnFit: true, cellStyle: { textAlign: 'right' } },
  },
  {
    accessorKey: 'issuedWarehouseName',
    header: '이슈발생센터',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'processMethod',
    header: '처리방법',
    cell: (info) => {
      const value = info.getValue<TypeReturnDisposalProcessMethod>();
      return RETURN_DISPOSAL_PROCESS_METHOD[value] ?? '-';
    },
    meta: { columnFit: true },
  },
  {
    accessorKey: 'processStatus',
    header: '처리상태',
    cell: (info) => {
      const value = info.getValue<TypeReturnDisposalProcessStatus>();
      return value ? <BadgeCellRenderer value={value} colorMap={PROCESS_STATUS_COLOR_MAP} /> : '-';
    },
    meta: { columnFit: true },
  },
];

export default getReturnDisposalClaimItemsColumns;
